/**
 * RM — ServicePilot design layer, ink + lime brand (matches landing + native apps):
 * warm off-white (#f5f6f8), white cards (border #e9ebf0, ~14px radius),
 * brand blue #0038a8, sun yellow #fcd116 accent, near-black #20242c text, Manrope body + Space Grotesk display.
 */
import type { ReactNode, Ref, UIEventHandler } from 'react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Plus, ArrowUpRight, ArrowRight, ArrowLeft, Search, ChevronDown, X } from 'lucide-react'

/* ---------- buttons ---------- */

// Touch target (spec 007 T008): on coarse pointers (phones/tablets) every button gets a
// ≥44px hit area — Apple/Material minimum. `pointer:fine` (mouse) keeps the tighter desktop
// heights, so nothing about the desktop layout changes.
const touch = "[@media(pointer:coarse)]:min-h-[44px]"
export const btnPrimary =
  `inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-brand px-4 text-[14px] font-medium text-brand-fg shadow-[0_3px_10px_var(--shadow-color)] transition-colors hover:bg-brand-hover disabled:opacity-50 ${touch}`
export const btnSecondary =
  `inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-token bg-token-surface px-3.5 text-[14px] font-medium text-token-primary transition-colors hover:bg-token-hover disabled:opacity-50 ${touch}`
export const btnDark =
  `inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-token-primary px-4 text-[14px] font-medium text-token-inverse transition-colors hover:bg-brand-hover disabled:opacity-50 ${touch}`
export const btnGhost =
  `inline-flex h-8 items-center gap-1 rounded-lg px-2 text-[14px] font-medium text-token-secondary transition-colors hover:bg-token-hover hover:text-token-primary ${touch}`

/**
 * Row action buttons that reveal on hover on pointer devices, but stay VISIBLE
 * on touch devices (which have no hover) and when focused via keyboard.
 * Use instead of the `opacity-0 group-hover:opacity-100` pattern, which hides
 * actions permanently on phones/tablets.
 */
export const hoverActions =
  "transition [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-within:opacity-100"

/* ---------- status ---------- */

export function statusTone(status: string): { dot: string; text: string; bg: string } {
  const s = (status || '').toLowerCase()
  if (s.includes('complete') || s.includes('done') || s.includes('paid') || (s.includes('active') && !s.includes('inactive')) || s.includes('accept'))
    return { dot: 'bg-token-success', text: 'text-token-success', bg: 'bg-token-success-surface' }
  if (s.includes('progress') || s.includes('sent') || s.includes('issued') || s.includes('scheduled') || s.includes('trial'))
    return { dot: 'bg-token-info', text: 'text-token-info', bg: 'bg-token-info-surface' }
  if (s.includes('overdue') || s.includes('past_due') || s.includes('past due') || s.includes('unpaid') || s.includes('cancel') || s.includes('fail') || s.includes('void') || s.includes('declin') || s.includes('hold'))
    return { dot: 'bg-token-danger', text: 'text-token-danger', bg: 'bg-token-danger-surface' }
  if (s.includes('inactive') || s.includes('archiv') || s.includes('draft') || s.includes('not opened'))
    return { dot: 'bg-token-muted', text: 'text-token-secondary', bg: 'bg-token-hover' }
  return { dot: 'bg-token-warning', text: 'text-token-warning', bg: 'bg-token-warning-surface' } // pending / viewed
}

export function StatusPill({ status, label }: { status: string; label?: string }) {
  const t = statusTone(status)
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold', t.bg, t.text)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />
      <span className="capitalize">{label ?? status ?? '—'}</span>
    </span>
  )
}

/* ---------- page chrome ---------- */

export function Page({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-full bg-canvas">
      <div className="max-w-[1320px] px-4 pb-12 pt-5 sm:px-6 sm:pt-6 lg:px-8">{children}</div>
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{eyebrow}</p>}
        <h1 className="font-display mt-1 text-[18px] font-semibold leading-tight text-token-primary">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-token-secondary">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:justify-end">{actions}</div>}
    </div>
  )
}

/** The shared control strip used above queues, registers and planners. */
export function Toolbar({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>
}

/** A split queue and inspector. The caller owns responsive fallbacks and route selection. */
export function Workspace({ list, inspector, className, railCollapsed = false, listRef, onListScroll }: {
  list: ReactNode
  inspector: ReactNode
  className?: string
  railCollapsed?: boolean
  listRef?: Ref<HTMLElement>
  onListScroll?: UIEventHandler<HTMLElement>
}) {
  return (
    <section className={cn('grid min-h-[560px] overflow-hidden rounded-[16px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)] min-[1200px]:h-[calc(100dvh-220px)]', railCollapsed ? 'min-[1200px]:grid-cols-[minmax(0,1fr)]' : 'min-[1200px]:grid-cols-[320px_minmax(0,1fr)] min-[1280px]:grid-cols-[minmax(360px,400px)_minmax(0,1fr)]', className)}>
      <aside ref={listRef} onScroll={onListScroll} className={cn('min-h-0 overflow-y-auto border-b border-token-subtle min-[1200px]:border-b-0 min-[1200px]:border-r', railCollapsed && 'hidden')}>{list}</aside>
      <div className="min-h-0 overflow-y-auto bg-token-surface">{inspector}</div>
    </section>
  )
}

/* ---------- document builder ---------- */

/**
 * The shell both the quote and the invoice builder sit in: a sticky toolbar over a full-bleed
 * document column. It is deliberately NOT `<Page>`. A quote or an invoice is a document the
 * customer will see, so it gets the whole width and its actions stay reachable while you scroll a
 * long line-item list, rather than scrolling away with a page header.
 *
 * `back` is the left-hand escape hatch, `actions` the right-hand controls, `strip` an optional
 * full-width row between the toolbar and the document (the message thread lives there).
 */
export function DocumentBuilder({ back, actions, strip, children }: {
  back: ReactNode
  actions: ReactNode
  strip?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-canvas">
      <div className="sticky top-0 z-10 border-b border-token-subtle bg-token-surface/85 backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-6">
          {back}
          <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>
        </div>
      </div>
      {strip && <div className="mx-auto max-w-4xl px-6 pt-5">{strip}</div>}
      <div className="mx-auto max-w-4xl px-6 py-8">{children}</div>
    </div>
  )
}

/** The white page the document itself is printed on, inside a `<DocumentBuilder>`. */
export function DocumentSheet({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-[14px] border border-token-subtle bg-token-surface p-4 shadow-[var(--surface-shadow)] sm:p-8 lg:p-10', className)}>
      {children}
    </div>
  )
}

/** The toolbar's back link. Same shape on every builder so the escape hatch never moves. */
export function BuilderBack({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-token-secondary hover:text-token-primary">
      <ArrowLeft className="h-4 w-4" /> {label}
    </button>
  )
}

/* ---------- card / section ---------- */

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn('overflow-hidden rounded-[14px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)]', className)}>{children}</section>
  )
}

export function CardHeader({ title, count, action }: { title: string; count?: number; action?: ReactNode }) {
  return (
    <header className="flex items-center justify-between border-b border-token-subtle px-5 py-3.5">
      <div className="flex items-baseline gap-2">
        <h2 className="font-display text-[15px] font-semibold text-token-primary">{title}</h2>
        {typeof count === 'number' && <span className="text-[13px] font-bold text-token-muted tabular-nums">{count}</span>}
      </div>
      {action}
    </header>
  )
}

export function ViewAll({ onClick }: { onClick?: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 text-[13px] font-semibold text-token-secondary transition-colors hover:text-brand">
      View all <ArrowRight className="h-3.5 w-3.5" />
    </button>
  )
}

/* ---------- rows ---------- */

export function Row({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} className="group flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-token-hover">
      {children}
    </button>
  )
}

export function RowChevron() {
  return <ArrowUpRight className="h-4 w-4 text-token-muted transition-colors group-hover:text-brand" />
}

export function Rows({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-token-subtle">{children}</ul>
}

/** Loading placeholder for a detail page (spec 007 T009): a title bar + two card blocks. */
export function DetailSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-7 w-56 rounded-[8px] bg-canvas-subtle" />
      <div className="h-40 rounded-[16px] bg-canvas-subtle" />
      <div className="h-24 rounded-[16px] bg-canvas-subtle" />
    </div>
  )
}

export function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <ul className="divide-y divide-token-subtle">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="flex items-center justify-between px-5 py-3.5">
          <div className="h-3.5 w-44 animate-pulse rounded bg-canvas-subtle" />
          <div className="h-4 w-16 animate-pulse rounded-full bg-canvas-subtle" />
        </li>
      ))}
    </ul>
  )
}

export function EmptyState({ label, cta, onCta }: { label: string; cta?: string; onCta?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-8 text-center">
      <p className="text-[13px] text-token-secondary">{label}</p>
      {cta && (
        <button onClick={onCta} className={btnSecondary}>
          <Plus className="h-3.5 w-3.5" /> {cta}
        </button>
      )}
    </div>
  )
}

/* ---------- toolbar ---------- */

export function SearchInput({ value, onChange, placeholder = 'Search…' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="relative max-w-sm flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-token-muted" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-[10px] border border-token bg-token-surface pl-9 pr-3 text-[13.5px] text-token-primary outline-none transition-colors placeholder:text-token-muted focus:border-token-strong [@media(pointer:coarse)]:h-11"
      />
    </div>
  )
}

export type Chip = { id: string; label: string; count?: number }

export function FilterChips({ chips, active, onChange }: { chips: Chip[]; active: string; onChange: (id: string) => void }) {
  return (
    <TabPills chips={chips} active={active} onChange={onChange} />
  )
}

/* ---------- Tradepilot tab pills (light track, white active) ---------- */
export function TabPills({ chips, active, onChange }: { chips: Chip[]; active: string; onChange: (id: string) => void }) {
  return (
    <div className="inline-flex max-w-full items-center gap-1.5 overflow-x-auto rounded-[10px] border border-token-subtle bg-token-hover p-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {chips.map((c) => {
        const on = c.id === active
        return (
          <button
            key={c.id}
            onClick={() => onChange(c.id)}
            className={cn(
              'inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-[7px] px-3.5 py-[7px] text-[13px] font-bold transition-colors [@media(pointer:coarse)]:min-h-[44px]',
              on ? 'bg-token-surface text-token-primary shadow-[0_1px_2px_var(--shadow-color)]' : 'text-token-muted hover:text-token-secondary',
            )}
          >
            {c.label}
            {typeof c.count === 'number' && <span className={cn('tabular-nums', on ? 'text-token-muted' : 'text-token-muted')}>· {c.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

/* ---------- KPI tile (label / big Space-Grotesk number / delta) ---------- */
export function KpiCard({ label, value, delta, tone = 'ink' }: {
  label: string; value: ReactNode; delta?: ReactNode; tone?: 'ink' | 'green' | 'red' | 'orange'
}) {
  const valTone = tone === 'green' ? 'text-token-success' : tone === 'red' ? 'text-token-danger' : tone === 'orange' ? 'text-token-warning' : 'text-token-primary'
  return (
    <div className="rounded-[14px] border border-transparent bg-token-surface p-[18px] shadow-[var(--surface-shadow)]">
      <p className="text-[13px] font-semibold text-token-secondary">{label}</p>
      <p className={cn('font-display mt-2 text-[28px] font-bold leading-none tracking-[-0.01em] tabular-nums', valTone)}>{value}</p>
      {delta && <p className="mt-2 text-[12px] font-bold">{delta}</p>}
    </div>
  )
}

/* ---------- avatar with colour-cycled initials ---------- */
const AVATARS = [
  { bg: 'bg-token-selected', text: 'text-brand' },
  { bg: 'bg-token-info-surface', text: 'text-token-info' },
  { bg: 'bg-token-success-surface', text: 'text-token-success' },
  { bg: 'bg-token-hover', text: 'text-token-secondary' },
  { bg: 'bg-token-warning-surface', text: 'text-token-warning' },
]
export function Avatar({ name, seed = 0, className }: { name?: string | null; seed?: number; className?: string }) {
  const initials = (name ?? '?').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?'
  const t = AVATARS[Math.abs(seed) % AVATARS.length]
  return (
    <span className={cn('inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full text-[12px] font-bold', t.bg, t.text, className)}>
      {initials}
    </span>
  )
}

/* ---------- pagination (server-side .range) ---------- */
export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const last = Math.max(0, Math.ceil(total / pageSize) - 1)
  if (total <= pageSize) return null
  const from = total === 0 ? 0 : page * pageSize + 1
  const to = Math.min(total, (page + 1) * pageSize)
  const btn = 'inline-flex h-8 items-center rounded-[8px] border border-token bg-token-surface px-3 text-[12.5px] font-semibold text-token-secondary transition-colors enabled:hover:border-token-strong enabled:hover:text-brand disabled:opacity-40 [@media(pointer:coarse)]:min-h-[44px]'
  return (
    <div className="flex items-center justify-between border-t border-token-subtle px-5 py-3">
      <span className="text-[12.5px] text-token-secondary tabular-nums">{from}–{to} of {total}</span>
      <div className="flex items-center gap-1.5">
        <button className={btn} disabled={page <= 0} onClick={() => onPage(page - 1)}>Prev</button>
        <span className="px-1 text-[12.5px] tabular-nums text-token-muted">{page + 1}/{last + 1}</span>
        <button className={btn} disabled={page >= last} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  )
}

/* ---------- grid "table" (matches the comp: CSS-grid rows, not <table>) ----------
   On narrow screens the fixed-track grid can't fit, so the whole table scrolls
   horizontally inside a min-width track (min-width only bites below `minW`, so
   desktop is unaffected). */
export function Table({ cols, header, children, minW = 720, className }: { cols: string; header: ReactNode; children: ReactNode; minW?: number; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-[16px] border border-transparent bg-token-surface shadow-[var(--surface-shadow)]', className)}>
      <div className="overflow-x-auto">
        <div style={{ minWidth: minW }}>
          {/* Column headings take the middle value, not the lightest: they label data you are
              reading, so they are not "genuinely low priority". At 11px the muted grey sits around
              3.3:1 on white, under the 4.5:1 AA needs for small text; #667085 clears it. */}
          <div className="grid items-center gap-3.5 border-b border-token-subtle px-[22px] py-[13px] text-[11px] font-semibold uppercase tracking-[0.06em] text-token-secondary" style={{ gridTemplateColumns: cols }}>
            {header}
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}
export function Trow({ cols, onClick, children, last, selected = false }: { cols: string; onClick?: () => void; children: ReactNode; last?: boolean; selected?: boolean }) {
  return (
    <div
      onClick={onClick}
      onKeyDown={(event) => { if (onClick && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onClick() } }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-current={selected ? 'page' : undefined}
      className={cn('grid items-center gap-3.5 px-[22px] py-4 text-[13.5px] transition-colors', !last && 'border-b border-token-subtle', onClick && 'cursor-pointer hover:bg-token-hover', selected && 'border-l-[3px] border-l-brand bg-token-selected')}
      style={{ gridTemplateColumns: cols }}
    >
      {children}
    </div>
  )
}

/* ---------- combobox (spec 040) ---------- */

/**
 * A searchable single-select, shaped as a drop-in for the `<select>` elements it replaces.
 *
 * Why this exists: `use-clients.ts` deliberately pages through EVERY contact, so a customer with
 * 1,615 clients rendered 1,615 `<option>` nodes in each client dropdown. Correct data, unusable
 * control. Filtering alone is not the fix either — a 1,615-node div list is the same problem in a
 * different element — so the rendered set is capped and the rest is reachable by typing.
 *
 * Implements the ARIA combobox pattern rather than approximating it: the input owns
 * `role="combobox"`, the popup is a real `listbox` of `option`s, and the highlight is published
 * through `aria-activedescendant` so a screen reader follows the arrow keys.
 */
export function Combobox({
  value, onChange, options, placeholder = 'Search…', disabled, allowClear = true, id, className, maxRender = 100,
}: {
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  id?: string
  className?: string
  maxRender?: number
}) {
  const reactId = useId()
  const listId = `${id ?? reactId}-listbox`
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selected = options.find((o) => o.value === value) ?? null

  // Case-insensitive substring, not prefix: people search for "harding" in "Bill Harding".
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])
  const shown = matches.slice(0, maxRender)
  const hidden = matches.length - shown.length

  // The input shows the SELECTED label when closed and the QUERY when open. Without this the
  // control either forgets what is selected or refuses to let you type over it.
  const display = open ? query : (selected?.label ?? '')

  useEffect(() => { setActive(0) }, [query, open])

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  // Close on outside click. Pointerdown rather than click, so selecting inside a dialog does not
  // race the dialog's own handlers.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) { setOpen(false); setQuery('') }
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const commit = (v: string) => { onChange(v); setOpen(false); setQuery(''); inputRef.current?.blur() }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); e.preventDefault(); return }
    if (!open) return
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); break
      case 'ArrowUp': e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); break
      case 'Home': e.preventDefault(); setActive(0); break
      case 'End': e.preventDefault(); setActive(shown.length - 1); break
      case 'Enter': e.preventDefault(); if (shown[active]) commit(shown[active].value); break
      // Escape restores the previous selection rather than clearing it, which is what a native
      // select does and what people expect.
      case 'Escape': e.preventDefault(); setOpen(false); setQuery(''); break
      case 'Tab': setOpen(false); setQuery(''); break
    }
  }

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={display}
          placeholder={selected ? selected.label : placeholder}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={cn(
            'h-10 w-full rounded-[10px] border border-token bg-token-surface pl-3 pr-8 text-[14px] text-token-primary outline-none',
            'placeholder:text-token-muted focus:border-token-strong disabled:cursor-not-allowed disabled:bg-token-disabled',
          )}
        />
        {allowClear && selected && !disabled ? (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => { onChange(''); setQuery(''); inputRef.current?.focus() }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-token-muted transition-colors hover:text-brand"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-token-muted" />
        )}
      </div>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-[280px] w-full overflow-y-auto rounded-[10px] border border-token bg-token-raised py-1 shadow-[0_8px_24px_var(--shadow-color)]"
        >
          {shown.length === 0 && (
            <li className="px-3 py-2.5 text-[13.5px] text-token-muted">No matches</li>
          )}
          {shown.map((o, i) => (
            <li
              key={o.value}
              id={`${listId}-${i}`}
              data-idx={i}
              role="option"
              aria-selected={o.value === value}
              onPointerDown={(e) => { e.preventDefault(); commit(o.value) }}
              onPointerEnter={() => setActive(i)}
              className={cn(
                'cursor-pointer px-3 py-2 text-[13.5px] text-token-primary',
                i === active && 'bg-token-hover',
                o.value === value && 'font-semibold',
              )}
            >
              {o.label}
            </li>
          ))}
          {hidden > 0 && (
            <li className="border-t border-token-subtle px-3 py-2 text-[12px] text-token-muted">
              {hidden.toLocaleString()} more. Keep typing to narrow it down.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/* ---------- confirm dialog (spec 040) ---------- */

/**
 * Confirmation for destructive actions, replacing `window.confirm`.
 *
 * Built on the native `<dialog>` + `showModal()` on purpose. The platform gives the focus trap,
 * Escape-to-close, the inert backdrop and top-layer stacking for free — which is exactly the set of
 * things the seven hand-rolled `fixed inset-0` overlays in this app never got, none of which
 * carried `role="dialog"` or `aria-modal` either.
 */
export function ConfirmDialog({
  open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'default', busy = false,
  onConfirm, onCancel,
}: {
  open: boolean
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])

  // Escape fires `cancel` on a native dialog. Route it through onCancel so the caller's state stays
  // in step; otherwise the dialog closes itself and `open` stays true, and it never reopens.
  useEffect(() => {
    const d = ref.current
    if (!d) return
    const onNativeCancel = (e: Event) => { e.preventDefault(); if (!busy) onCancel() }
    d.addEventListener('cancel', onNativeCancel)
    return () => d.removeEventListener('cancel', onNativeCancel)
  }, [busy, onCancel])

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirm-title"
      className="m-auto w-[min(420px,calc(100vw-2rem))] rounded-[14px] border border-token bg-token-raised p-0 text-token-primary backdrop:bg-[var(--scrim)]"
    >
      <div className="px-5 pb-4 pt-5">
        <h2 id="confirm-title" className="font-display text-[16px] font-semibold text-token-primary">{title}</h2>
        {body && <div className="mt-2 text-[13.5px] leading-relaxed text-token-secondary">{body}</div>}
      </div>
      <div className="flex justify-end gap-2 border-t border-token-subtle px-5 py-3.5">
        <button type="button" className={btnGhost} disabled={busy} onClick={onCancel}>{cancelLabel}</button>
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className={cn(
            'inline-flex h-9 items-center justify-center rounded-[10px] px-3.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            tone === 'danger'
              ? 'bg-token-danger text-token-inverse hover:opacity-90'
              : 'bg-brand text-brand-fg hover:bg-brand-hover',
          )}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </dialog>
  )
}

/**
 * Promise-shaped confirmation, so a call site reads the way `window.confirm` did:
 *
 *   const [confirm, confirmDialog] = useConfirm()
 *   if (!(await confirm({ title: 'Remove Sam?', tone: 'danger' }))) return
 *   …
 *   return <>{confirmDialog}…</>
 *
 * The alternative was rewriting every guarded handler into a pending-action state machine, which
 * is a lot of churn for no behavioural gain and a lot of places to get the cancel path wrong.
 */
export function useConfirm() {
  const [opts, setOpts] = useState<
    { title: string; body?: ReactNode; confirmLabel?: string; cancelLabel?: string; tone?: 'default' | 'danger' } | null
  >(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback((o: NonNullable<typeof opts>) => {
    setOpts(o)
    return new Promise<boolean>((resolve) => { resolver.current = resolve })
  }, [])

  const settle = useCallback((ok: boolean) => {
    // Resolve BEFORE clearing, and null the resolver, so a double-click cannot resolve twice and
    // run the guarded action two times.
    const r = resolver.current
    resolver.current = null
    setOpts(null)
    r?.(ok)
  }, [])

  const element = (
    <ConfirmDialog
      open={!!opts}
      title={opts?.title ?? ''}
      body={opts?.body}
      confirmLabel={opts?.confirmLabel}
      cancelLabel={opts?.cancelLabel}
      tone={opts?.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  )

  return [confirm, element] as const
}
